import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { allGuides } from './guides';
import FeatureGuide from './FeatureGuide';

// ─── API helpers ─────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || 'https://app.aideyapp.com/api';

function getToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || null;
}

async function fetchProgress() {
  const token = getToken();
  const res = await fetch(`${API_BASE}/tours/progress`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`tours/progress ${res.status}`);
  const data = await res.json();
  return data.progress || {};
}

async function saveProgress(tourId, status) {
  const token = getToken();
  await fetch(`${API_BASE}/tours/progress`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify({ tourId, status, currentStep: 0 }),
  }).catch(err => console.warn('[tours] saveProgress failed:', err));
}

// ─── Context ──────────────────────────────────────────────────────────────────
const TourContext = createContext(null);

export function TourProvider({ children }) {
  // progressMap: { [tourId]: { status, currentStep, completedAt } }
  // status values: 'in_progress' | 'skipped_once' | 'skipped' | 'completed'
  const [progressMap, setProgressMap] = useState({});
  const [loaded, setLoaded]           = useState(false);
  const [guideOpen, setGuideOpen]     = useState(null); // tourId | null

  // Ref kept in sync with progressMap so openGuide can read current status
  // without progressMap in its dep array (avoids recreating on every status change).
  const progressMapRef = useRef(progressMap);
  useEffect(() => { progressMapRef.current = progressMap; }, [progressMap]);

  // Snapshot of the status a guide had before it was opened this session.
  // Used by closeGuide to decide which final status to write.
  const preOpenStatusRef = useRef(null);

  // Load tour progress once we have an auth token.
  // TourProvider mounts before the user is authenticated, so we poll for a
  // token rather than fetching immediately (which would 401 and incorrectly
  // set loaded=true with an empty map, firing every guide on every refresh).
  useEffect(() => {
    let cancelled = false;

    function attempt() {
      if (!getToken()) return false; // no token yet — keep polling
      fetchProgress()
        .then(map => {
          if (!cancelled) { setProgressMap(map); setLoaded(true); }
        })
        .catch(() => {}); // on failure keep guides dormant; interval already cleared
      return true; // had a token; stop polling regardless of fetch result
    }

    if (!attempt()) {
      const iv = setInterval(() => { if (attempt()) clearInterval(iv); }, 500);
      return () => { cancelled = true; clearInterval(iv); };
    }
    return () => { cancelled = true; };
  }, []);

  // hasSeenTour: controls auto-fire gate only.
  // Returns true (suppress auto-fire) only for terminal statuses.
  // skipped_once, in_progress, and no record all allow auto-fire.
  const hasSeenTour = useCallback(
    tourId => {
      const s = progressMap[tourId]?.status;
      return s === 'completed' || s === 'skipped';
    },
    [progressMap]
  );

  // openGuide: shows the guide for tourId.
  // Auto-fire passes no options; HelpButton passes { force: true } to open
  // regardless of status (so ? always works).
  const openGuide = useCallback(
    (tourId, { force = false } = {}) => {
      if (!allGuides[tourId]) return;
      if (!force && hasSeenTour(tourId)) return;

      const currentStatus = progressMapRef.current[tourId]?.status ?? null;
      preOpenStatusRef.current = currentStatus;

      // Write in_progress to prevent a mid-guide page-refresh from re-firing
      // the guide. Skip if already in a terminal state (force-opening a completed
      // or permanently skipped guide should not downgrade its status).
      if (currentStatus !== 'completed' && currentStatus !== 'skipped') {
        saveProgress(tourId, 'in_progress');
        setProgressMap(prev => ({
          ...prev,
          [tourId]: { ...prev[tourId], status: 'in_progress' },
        }));
      }

      setGuideOpen(tourId);
    },
    [hasSeenTour]
  );

  // closeGuide: called on X, backdrop tap, or Got it.
  // completed = true  → "Got it" on the final step   → status: completed
  // completed = false → X or backdrop                → two-strike logic:
  //   • first real dismiss (null / not_started)      → skipped_once
  //   • second real dismiss (skipped_once / in_prog) → skipped (permanent)
  //   • force-opened a terminal guide then dismissed → restore original (no write)
  const closeGuide = useCallback(
    (completed = false) => {
      const tourId = guideOpen;
      if (!tourId) return;

      const pre = preOpenStatusRef.current;
      let status;

      if (completed) {
        status = 'completed';
      } else if (pre === 'completed' || pre === 'skipped') {
        // Guide was force-opened via ? — restore original, no change
        status = pre;
      } else if (pre === 'skipped_once' || pre === 'in_progress') {
        // Second real dismiss (or interrupted guide explicitly closed) → retire
        status = 'skipped';
      } else {
        // First real dismiss (no record, not_started, null) → one more chance
        status = 'skipped_once';
      }

      if (status !== pre) {
        saveProgress(tourId, status);
      }
      setProgressMap(prev => ({
        ...prev,
        [tourId]: { status, currentStep: 0 },
      }));
      setGuideOpen(null);
    },
    [guideOpen]
  );

  const currentSteps = guideOpen ? (allGuides[guideOpen]?.steps || []) : [];

  return (
    <TourContext.Provider value={{ openGuide, hasSeenTour, loaded }}>
      {children}
      <FeatureGuide
        steps={currentSteps}
        open={guideOpen !== null}
        onClose={() => closeGuide(false)}
        onComplete={() => closeGuide(true)}
      />
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used inside <TourProvider>');
  return ctx;
}
